import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const EmployeeAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/employees", token);
  const { shop } = useTenant();
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user;
  const isAdmin = Boolean(currentUser?.is_superuser);
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shops-all-nopagination"],
    "shop/shop/?page_size=all",
    token,
    { enabled: open && isAdmin }
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [salary, setSalary] = useState("");
  const [age, setAge] = useState("");
  const [contractDate, setContractDate] = useState("");
  const [contractDurations, setContractDurations] = useState("");
  const [selectedShop, setSelectedShop] = useState("");

  useEffect(() => {
    if (isAdmin) {
      setSelectedShop("");
    } else {
      setSelectedShop(shop?.id ? String(shop.id) : "");
    }
  }, [isAdmin, shop?.id, open]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", isAdmin ? selectedShop : String(shop?.id || ""));
    formData.append("name", name);
    formData.append("email", email);
    formData.append("contact", contact);
    formData.append("address", address);
    formData.append("salary", salary);
    formData.append("age", age);
    formData.append("contract_date", contractDate);
    formData.append("contract_durations", contractDurations);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setEmail("");
      setContact("");
      setAddress("");
      setSalary("");
      setAge("");
      setContractDate("");
      setContractDurations("");
      setSelectedShop("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("employee.addEmployee")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {isAdmin ? (
            <TextField
              select
              margin="dense"
              label={t("employee.selectShop")}
              fullWidth
              required
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value)}
            >
              {shopLoading ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                shops.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          ) : (
            <TextField
              margin="dense"
              label={t("employee.selectShop")}
              fullWidth
              value={shop?.name || ""}
              InputProps={{ readOnly: true }}
            />
          )}
          <TextField
            margin="dense"
            label={t("employee.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.email")}
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.contact")}
            fullWidth
            required
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.address")}
            fullWidth
            required
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.salary")}
            type="number"
            fullWidth
            required
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.age")}
            type="number"
            fullWidth
            required
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.contractDate")}
            type="date"
            fullWidth
            required
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t("employee.contractDurations")}
            type="date"
            fullWidth
            required
            value={contractDurations}
            onChange={(e) => setContractDurations(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("employee.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !(isAdmin ? selectedShop : shop?.id) ||
            !name ||
            !email ||
            !contact ||
            !address ||
            !salary ||
            !age ||
            !contractDate ||
            !contractDurations
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("employee.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeAdd;
